/* facts-lp.js · FACTS LP-Kit v2 · Verhalten aller LP-Sections.
   ------------------------------------------------------------------
   Regeln aus dem Section-Standard §8:
   - IntersectionObserver statt Scroll-Listener, wo es geht. Ein Scroll-
     Listener misst sonst pro Ereignis das Layout.
   - Ein Custom Element je Section, Name = Dateiname der Section. Der
     Browser ruft connectedCallback beim Editor-Rerender selbst auf, es
     braucht also keinen shopify:section:load-Handler. Teardown gehoert
     nach disconnectedCallback.
   - prefers-reduced-motion: Endzustand sofort, keine Animation.
   - Endwerte stehen im HTML. Ohne JavaScript ist die Seite vollstaendig
     lesbar; das Skript animiert nur.
   In v1 lag dieses Verhalten als sieben Inline-Skripte in den Sections.
   Ein Asset laesst sich ueber Seitenaufrufe hinweg cachen. */
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HAS_IO = 'IntersectionObserver' in window;

  /* ---------- gemeinsame Einblendung ------------------------------------ */
  function revealAll(root) {
    root.querySelectorAll('[data-lp-rv]').forEach(function (el) { el.classList.add('is-in'); });
    root.querySelectorAll('[data-lp-bar]').forEach(function (el) {
      el.style.width = (parseFloat(el.getAttribute('data-lp-bar')) || 0) + '%';
    });
  }

  function makeReveal(root) {
    if (!HAS_IO || REDUCE) { revealAll(root); return null; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        el.classList.add('is-in');
        el.querySelectorAll('[data-lp-bar]').forEach(function (b) {
          b.style.width = (parseFloat(b.getAttribute('data-lp-bar')) || 0) + '%';
        });
        io.unobserve(el);
      });
    }, { threshold: 0.16 });
    root.querySelectorAll('[data-lp-rv]').forEach(function (el) { io.observe(el); });
    return io;
  }

  /* ---------- Grund ------------------------------------------------------
     Zusaetzlich zur Einblendung setzt der Grund data-inview, solange er im
     Bild ist. Daran haengt der gefuellte Punkt auf der Leiter - die Leiter
     zeigt damit den Fortschritt durch die sieben Gruende. */
  if (!customElements.get('facts-lp-beat')) {
    customElements.define('facts-lp-beat', class extends HTMLElement {
      connectedCallback() {
        this._rv = makeReveal(this);
        if (!HAS_IO) { this.setAttribute('data-inview', ''); return; }
        var self = this;
        this._io = new IntersectionObserver(function (es) {
          es.forEach(function (e) {
            if (e.isIntersecting) self.setAttribute('data-inview', '');
            else self.removeAttribute('data-inview');
          });
        }, { rootMargin: '-45% 0px -45% 0px' });
        this._io.observe(this);
      }
      disconnectedCallback() {
        if (this._rv) this._rv.disconnect();
        if (this._io) this._io.disconnect();
      }
    });
  }

  /* ---------- einfache Sections mit reiner Einblendung -------------------- */
  ['facts-lp-toc', 'facts-lp-offer', 'facts-lp-faq',
   'facts-lp-credbar', 'facts-lp-pricemath', 'facts-lp-plans'].forEach(function (tag) {
    if (customElements.get(tag)) return;
    customElements.define(tag, class extends HTMLElement {
      connectedCallback() { this._rv = makeReveal(this); }
      disconnectedCallback() { if (this._rv) this._rv.disconnect(); }
    });
  });

  /* ---------- Sticky-CTA -------------------------------------------------
     Ohne Scroll-Listener: drei Beobachter statt einer Rechnung pro Ereignis.
     Sichtbar, sobald der Startbereich nach oben aus dem Bild ist, und
     unsichtbar, solange das Angebot oder der Schluss im Bild ist. So
     konkurriert die Leiste nie mit dem eigentlichen Angebot. */
  if (!customElements.get('facts-lp-sticky')) {
    customElements.define('facts-lp-sticky', class extends HTMLElement {
      connectedCallback() {
        this._passed = false; this._blocked = 0;
        if (!HAS_IO) return;
        var self = this;
        var start = document.getElementById(this.getAttribute('data-start') || '');
        var blockers = (this.getAttribute('data-block') || '').split(',')
          .map(function (id) { return document.getElementById(id.trim()); })
          .filter(Boolean);

        if (start) {
          this._ioA = new IntersectionObserver(function (es) {
            es.forEach(function (e) {
              self._passed = !e.isIntersecting && e.boundingClientRect.top < 0;
              self._update();
            });
          }, { threshold: 0 });
          this._ioA.observe(start);
        } else { this._passed = true; }

        if (blockers.length) {
          this._ioB = new IntersectionObserver(function (es) {
            es.forEach(function (e) { e.target._lpBlocking = e.isIntersecting; });
            self._blocked = blockers.filter(function (b) { return b._lpBlocking; }).length;
            self._update();
          }, { threshold: 0.05 });
          blockers.forEach(function (b) { self._ioB.observe(b); });
        }
        this._update();
      }
      _update() {
        var on = this._passed && this._blocked === 0;
        this.classList.toggle('is-on', on);
        this.setAttribute('aria-hidden', on ? 'false' : 'true');
      }
      disconnectedCallback() {
        if (this._ioA) this._ioA.disconnect();
        if (this._ioB) this._ioB.disconnect();
      }
    });
  }

  /* ---------- Seiten-Skript ----------------------------------------------
     Sitzt im Masthead, weil der Masthead der Singleton ist, der immer als
     erste Section laeuft. Drei Aufgaben: UTM-Durchreichung, Scroll-Tiefe,
     Klick-Ereignisse. Pixel und Clarity gehoeren ins Theme-Layout. */
  if (!customElements.get('facts-lp-page')) {
    customElements.define('facts-lp-page', class extends HTMLElement {
      connectedCallback() {
        var LP = this.getAttribute('data-lp') || '';
        this._lp = LP;
        this._decorate();
        this._onClick = this._click.bind(this);
        document.addEventListener('click', this._onClick, true);
        if (this.getAttribute('data-scroll') !== 'false') this._depth();
      }

      _decorate() {
        var params = new URLSearchParams(location.search), keep = [];
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid']
          .forEach(function (k) {
            var v = params.get(k);
            if (v) keep.push(k + '=' + encodeURIComponent(v));
          });
        if (this._lp) keep.push('utm_lp=' + encodeURIComponent(this._lp));
        if (!keep.length) return;
        document.querySelectorAll('a[data-utm]').forEach(function (a) {
          if (a.dataset.utmDone) return;
          a.dataset.utmDone = '1';
          a.href += (a.href.indexOf('?') > -1 ? '&' : '?') + keep.join('&');
        });
      }

      /* Scroll-Tiefe braucht die Scrollposition, also einen Listener. Er
         liest nur scrollY und zwei gecachte Hoehen und wird pro Bild
         hoechstens einmal ausgewertet - kein Layout pro Ereignis. */
      _depth() {
        var self = this, marks = [25, 50, 75, 95], fired = {}, ticking = false;
        function measure() {
          var h = document.documentElement;
          self._range = h.scrollHeight - h.clientHeight;
        }
        function read() {
          ticking = false;
          if (!self._range) return;
          var p = Math.round((window.scrollY / self._range) * 100);
          marks.forEach(function (m) {
            if (p >= m && !fired[m]) {
              fired[m] = true;
              try { window.fbq && fbq('trackCustom', 'AdvertorialScroll', { depth: m, lp: self._lp }); } catch (e) {}
              try { window.clarity && clarity('set', 'scroll_depth', String(m)); } catch (e) {}
            }
          });
        }
        this._onScroll = function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(read);
        };
        this._onResize = measure;
        measure();
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });
      }

      _click(ev) {
        var a = ev.target.closest && ev.target.closest('a');
        if (!a) return;
        if (a.dataset.buy) {
          try { window.fbq && fbq('trackCustom', 'AdvertorialBuyClick', { option: a.dataset.buy, lp: this._lp }); } catch (e) {}
        }
        if (a.dataset.toc) {
          try { window.fbq && fbq('trackCustom', 'ListicleIndexClick', { target: a.getAttribute('href'), lp: this._lp }); } catch (e) {}
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._onClick, true);
        if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
      }
    });
  }

  /* ---------- Sicherheitsnetz ---------------------------------------------
     Jede Section, die [data-lp-rv] benutzt, versteckt ihre Inhalte per CSS
     und verlaesst sich darauf, dass JavaScript .is-in nachtraegt. Fehlt der
     Custom-Element-Name in der Liste oben, bleibt die Section LEER: Hinter-
     grund, Rahmen und Hoehe rendern, der Inhalt nicht. Genau das ist am
     20.08.2026 bei facts-lp-credbar, facts-lp-pricemath und facts-lp-plans
     passiert, und im Theme-Editor sieht es aus wie eine leere Section.
     Deshalb laeuft zusaetzlich ein dokumentweiter Durchgang. Er ist
     idempotent: makeReveal beobachtet nur, und der Beobachter meldet sich
     nach dem ersten Treffer selbst ab. Eine neue Section kann damit nie
     wieder unsichtbar sein, auch wenn jemand vergisst, sie einzutragen. */
  function sweep(root) { makeReveal(root || document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sweep(); });
  } else {
    sweep();
  }
  document.addEventListener('shopify:section:load', function (e) { sweep(e.target); });
})();